import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { Form, Formik } from 'formik';

import { Modal, SingleSelect } from '@/common';
import { Button, Label, LABEL_TYPE, Spinner } from '@/components/generic';
import { ButtonVariant } from '@/components/generic/Button/button-config';
import { queryClient } from '@/configuration/react-query';
import { IMAGE, TEXT_OVERLAY } from '@/constants';
import { getSlotTypeTranslation } from '@/helpers';
import { showToast, ToastType } from '@/helpers/toast';
import { AddSlotValidationScheme } from '@/pages/Event/CeremonyTemplate/AddSlotValidationScheme';
import { MusicIcon, PhotoIcon, PlusIcon, SpeechIcon, VideoIcon } from '@/resources';
import { Playlists } from '@/resources/icons/sidebar';
import {
  useCreateAssetSlotMutation,
  useGetAllCeremonySlotTypes,
  useUpdateEventMutation
} from '@/services';
import { TestIds } from '@/shared/test-ids';
import { AddSlotProps, AssetSlotCreate, SlotType } from '@/types';

export const AddSlot = ({
  eventData,
  numberOfSlots,
  isTemplateChange,
  isOpenNotifyingUploadersModal,
  handleTemplateChange,
  hasCeremonySetupAccess
}: AddSlotProps) => {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const { eventId } = useParams();
  const [t] = useTranslation('common');
  const [notifyingUploadersModal, setNotifyingUploadersModal] = useState<boolean>(
    isOpenNotifyingUploadersModal as boolean
  );
  const [slotTypeId, setSlotTypeId] = useState<number>();
  // Held across the notify-uploaders prompt so a dropped asset survives the extra confirmation
  // step and still lands in the slot that gets created.
  const [pendingAssetId, setPendingAssetId] = useState<number | null>(null);

  const { data: slotTypes, isLoading } = useGetAllCeremonySlotTypes(
    eventData.organization.id,
    hasCeremonySetupAccess
  );

  const initialValues = {
    slotTypeId: -1
  };

  const { mutate: updateEvent } = useUpdateEventMutation(queryClient, {
    onSuccess: () => {
      showToast(ToastType.SUCCESS, t('eventUpdated'));
    },
    onError: (error) => {
      showToast(ToastType.ERROR, error.response.data.ErrorMessages[0].DisplayMessage);
    }
  });

  const { mutate: createSlot } = useCreateAssetSlotMutation(queryClient, {
    onSuccess: () => {
      setIsModalOpen(false);
      if (eventData?.ceremonyTemplate?.id !== undefined) {
        const payload = {
          ...eventData,
          ceremonyTemplateId: null,
          organizationId: eventData?.organization.id,
          isContentUploadConfirmed: true
        };
        updateEvent(payload);
      }
    }
  });

  const getOptions = () => {
    return slotTypes?.data?.items.map((type: SlotType) => {
      let icon = null;
      switch (type.assetType) {
        case 'TributeVideo':
        case 'Video':
          icon = <VideoIcon fill={type.iconColor} className='size-5' />;
          break;
        case 'CoverPhoto':
        case 'Image':
          icon = <PhotoIcon fill={type.iconColor} className='size-5' />;
          break;
        case 'Audio':
          icon = <MusicIcon fill={type.iconColor} className='size-5' />;
          break;
        case 'Text':
          icon = <SpeechIcon fill={type.iconColor} className='size-5' />;
          break;
        case 'Playlist':
          icon = <Playlists style={{ color: type.iconColor }} className='size-5' />;
          break;
      }

      return {
        value: type.id,
        label: getSlotTypeTranslation(type.id),
        icon: icon
      };
    }) as { value: number; label: string; icon?: JSX.Element }[];
  };

  // Returns undefined when the organization has no slot type for the dropped asset - the caller
  // must bail rather than post a slot with no type.
  const getSlotTypeIdByAssetType = (assetType: string): number | undefined => {
    const wantedAssetType = assetType === TEXT_OVERLAY ? IMAGE : assetType;
    return slotTypes?.data?.items.find((st: SlotType) => st.assetType === wantedAssetType)?.id;
  };

  const handleSubmit = (values: { slotTypeId: number; confirm: boolean; assetId?: number }) => {
    if (eventData?.isContentUploadConfirmed) {
      setNotifyingUploadersModal(true);
      setIsModalOpen(false);
      setPendingAssetId(values.assetId ?? null);
    } else {
      const payload = {
        eventId: Number(eventId),
        order: numberOfSlots + 1,
        slotTypeId: values.slotTypeId,
        assetId: values.assetId || null
      };
      createSlot(payload as AssetSlotCreate);
      setPendingAssetId(null);
    }
    setSlotTypeId(values.slotTypeId);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const raw = e.dataTransfer.getData('application/slotJson');

    if (!raw) {
      return;
    }

    let draggedItemData;

    try {
      draggedItemData = JSON.parse(raw);
    } catch {
      return;
    }

    if (!draggedItemData?.assetType || !draggedItemData?.id) {
      return;
    }

    const droppedSlotTypeId = getSlotTypeIdByAssetType(draggedItemData.assetType);

    if (!droppedSlotTypeId) {
      showToast(ToastType.ERROR, t('noSlotTypeForAsset'));
      return;
    }

    handleSubmit({
      slotTypeId: droppedSlotTypeId,
      confirm: true,
      assetId: draggedItemData.id
    });
  };

  const handleConfirmSubmit = (values: boolean) => {
    if (isTemplateChange) {
      handleTemplateChange(values);
    } else {
      const payload = {
        eventId: Number(eventId),
        order: numberOfSlots + 1,
        slotTypeId: slotTypeId,
        assetId: pendingAssetId,
        isNotifyingUploaders: values
      };
      createSlot(payload as AssetSlotCreate);

      setPendingAssetId(null);
      setNotifyingUploadersModal(false);
    }
  };

  return (
    <div
      className='flex aspect-square w-full cursor-pointer items-center justify-center rounded-2xl border border-primary-blue'
      data-testid={TestIds.eventCeremony.addSlotButton}
      onClick={() => setIsModalOpen(true)}
      onDragOver={(e) => e.preventDefault()}
      onDrop={handleDrop}>
      <PlusIcon className='size-9 text-primary-blue' />
      <Modal
        isOpen={isModalOpen || notifyingUploadersModal}
        onClose={() => {
          setPendingAssetId(null);
          notifyingUploadersModal ? setNotifyingUploadersModal(false) : setIsModalOpen(false);
        }}
        height='300px'
        modalClasses='w-full md:w-[600px] h-[300px]'>
        {isLoading ? (
          <Spinner />
        ) : notifyingUploadersModal ? (
          <div className='size-full px-6'>
            <Label text={t('addMedia')} type={LABEL_TYPE.H2} className='mb-6 font-semibold' />
            <Label
              text={t('addSlotMessage')}
              type={LABEL_TYPE.PARAGRAPH1}
              className='mb-6 font-semibold'
            />
            <div className='flex w-full justify-center gap-x-5'>
              <Button
                type='submit'
                handleOnClick={() => handleConfirmSubmit(true)}
                variant={ButtonVariant.PRIMARY}>
                {t('yes')}
              </Button>
              <Button
                type='submit'
                handleOnClick={() => handleConfirmSubmit(false)}
                variant={ButtonVariant.PRIMARY}>
                {t('no')}
              </Button>
            </div>
          </div>
        ) : (
          <div className='size-full px-6'>
            <Label text={t('addMedia')} type={LABEL_TYPE.H2} className='mb-6 font-semibold' />
            <Formik
              initialValues={initialValues as { slotTypeId: number; confirm: boolean }}
              validationSchema={AddSlotValidationScheme}
              onSubmit={handleSubmit}>
              {({ values, setFieldValue, errors, touched }) => (
                <Form className='flex flex-col gap-y-6'>
                  <div data-testid={TestIds.eventCeremony.slotTypeSelect}>
                    <SingleSelect
                      placeholder={t('selectType')}
                      id='slotTypeId'
                      label={t('type')}
                      options={getOptions()}
                      handleChange={(slotType) =>
                        setFieldValue('slotTypeId', slotType?.value ?? -1)
                      }
                      errorMessage={errors.slotTypeId}
                      isInvalid={!!errors.slotTypeId && touched.slotTypeId}
                      isRequired
                      textColor='var(--color-secondary-gray)'
                    />
                  </div>
                  <div className='flex w-full justify-end'>
                    <Button
                      type='submit'
                      disabled={values.slotTypeId < 1}
                      dataTestId={TestIds.eventCeremony.saveSlotButton}>
                      {t('save')}
                    </Button>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        )}
      </Modal>
    </div>
  );
};
